import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createReservation,
  deleteReservation,
  getReservationAuditLog,
  listReservations,
  patchReservation,
} from '../api/client';
import type { CreateReservationBody, PatchReservationBody } from '../api/client';
import type { ReservationStatus } from '../api/types';

export function useReservations(
  locationId: string | null,
  params?: {
    status?: ReservationStatus;
    from?: string;
    to?: string;
    limit?: number;
  },
) {
  return useQuery({
    queryKey: ['reservations', locationId, params],
    queryFn: () => listReservations(locationId!, { limit: 200, ...params }),
    enabled: locationId != null,
    staleTime: 2 * 60_000,
  });
}

export function useReservations3Day(locationId: string | null) {
  return useQuery({
    queryKey: ['reservations', '3day', locationId],
    queryFn: () => {
      const from = new Date();
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setDate(to.getDate() + 2);
      to.setHours(23, 59, 59, 999);
      return listReservations(locationId!, {
        from: from.toISOString(),
        to: to.toISOString(),
        limit: 200,
      });
    },
    enabled: locationId != null,
    staleTime: 2 * 60_000,
  });
}

export function useUpcomingReservations(locationId: string | null, days = 7) {
  return useQuery({
    queryKey: ['reservations', 'upcoming', locationId, days],
    queryFn: () => {
      const from = new Date();
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setDate(to.getDate() + days - 1);
      to.setHours(23, 59, 59, 999);
      return listReservations(locationId!, {
        from: from.toISOString(),
        to: to.toISOString(),
        limit: 200,
      });
    },
    enabled: locationId != null,
    staleTime: 2 * 60_000,
  });
}

export function useReservationAuditLog(reservationId: string | null) {
  return useQuery({
    queryKey: ['reservations', 'audit', reservationId],
    queryFn: () => getReservationAuditLog(reservationId!),
    enabled: reservationId != null,
    staleTime: 0,
  });
}

export function useCreateReservation(locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReservationBody) => createReservation(locationId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function usePatchReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchReservationBody }) =>
      patchReservation(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}

export function useDeleteReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteReservation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reservations'] });
    },
  });
}
